import { getTopicKey } from '#shared/chat'
import type { ChatId } from '#shared/types/Chat'
import { ChatTopicBase } from '#shared/types/Chat'
import { SkillFilterSortByType } from '#shared/types/FilterItem'
import type { OrgId, UserId } from '#shared/types/ids'
import type { SearchResults } from '#shared/types/search'
import type { Skill, SkillFormData, SkillId } from '#shared/types/Skill'
import { SkillType } from '#shared/types/Skill'
import type { UserProfile } from '#shared/types/User'
import { VisibilityChangeAction, VisibilityType } from '#shared/types/visibility'
import { formatDate, orderByDate } from '#shared/utils'
import { useFirebase } from '@/composables/firebase/useFirebase'
import { useTypesense } from '@/composables/typesense/useTypesense'
import { query, where } from '@/utils/firebase/lazyFirestore'
import chunk from 'lodash-es/chunk'

export function useSkills() {
    const { $firebase, collections, ensureClientFirebase } = useFirebase()
    const { keywordSearch, hybridSearch, loadFacets } = useTypesense()

    const getFirebaseContext = (context: string) => {
        ensureClientFirebase(`useSkills.${context}`)

        return {
            $firebase: $firebase!,
            collections: collections!
        }
    }

    const DEFAULT_PER_PAGE = 24
    const FEATURED_SKILLS_COUNT = 3

    /**
     * Search skills using keyword search
     * Note: For semantic/hybrid search, the Typesense collection needs to be
     * recreated with auto-embedding enabled. See scripts/typesense/01-create-collections.ts
     */
    const searchSkills = (
        query: string,
        options: Partial<any> = {},
        sortBy = SkillFilterSortByType.ALL
    ): Promise<SearchResults<Skill>> => {
        const sortField =
            sortBy === SkillFilterSortByType.UPCOMING
                ? 'startDate:asc'
                : sortBy === SkillFilterSortByType.PAST
                  ? 'startDate:desc'
                  : 'createdAt:desc'

        return keywordSearch('skills', query, {
            page: 0,
            per_page: DEFAULT_PER_PAGE,
            sort_by: sortField,
            // Use default text fields (title,description) - see useTypesense.ts queryByFields
            ...options
        })
    }

    /**
     * Search skills with semantic/personalized search
     * Uses hybrid search (embeddings + keywords) for better relevance
     */
    const searchSkillsPersonalized = async (
        query: string,
        options: Partial<any> & {
            userProfile?: {
                interests?: string[]
                growthAreas?: string[]
            }
            /** Pre-built personalization query (cached). Takes precedence over userProfile. */
            personalizationQuery?: string
        } = {},
        sortBy = SkillFilterSortByType.ALL
    ): Promise<SearchResults<Skill>> => {
        const sortField =
            sortBy === SkillFilterSortByType.UPCOMING
                ? 'startDate:asc'
                : sortBy === SkillFilterSortByType.PAST
                  ? 'startDate:desc'
                  : undefined

        // Use pre-built personalization query if provided (cached), otherwise build from profile
        let searchQuery = typeof query === 'string' ? query.trim() : ''
        if (!searchQuery && options.personalizationQuery) {
            searchQuery = options.personalizationQuery
            console.log('[Skills]: Using cached personalization query:', searchQuery.substring(0, 100))
        } else if (!searchQuery && options.userProfile) {
            // Build from user profile (legacy)
            const profileParts: string[] = []
            if (options.userProfile.interests?.length) {
                profileParts.push(options.userProfile.interests.join(' '))
            }
            if (options.userProfile.growthAreas?.length) {
                profileParts.push(options.userProfile.growthAreas.join(' '))
            }
            if (profileParts.length) {
                searchQuery = profileParts.join(' ')
                console.log('[Skills]: Using profile-based personalization:', searchQuery.substring(0, 100))
            }
        }

        // If we have a search query (typed or from profile), use hybrid search
        if (searchQuery) {
            return await hybridSearch('skills', searchQuery, {
                page: options.page || 0,
                per_page: options.hitsPerPage || DEFAULT_PER_PAGE,
                filterBy: options.filterBy,
                sort_by: sortField
            })
        }

        // Fallback to keyword search sorted by date
        return await keywordSearch('skills', '*', {
            page: options.page || 0,
            per_page: options.hitsPerPage || DEFAULT_PER_PAGE,
            filterBy: options.filterBy,
            sort_by: sortField || 'createdAt:desc'
        })
    }

    /**
     * Load skills facets from Typesense
     */
    const loadSkillsFacets = async (
        options: {
            facets?: string[]
            filters?: string
        } = {}
    ): Promise<any> => {
        return await loadFacets('skills', {
            facets: options.facets,
            filterBy: options.filters
        })
    }

    /**
     * Get featured skills
     */
    const getFeaturedSkills = async (hitsPerPage: number = 50, filters?: string, ruleContexts?: Array<string>) => {
        const results = await keywordSearch('skills', '', {
            hitsPerPage,
            filterBy: filters
        })
        return results.hits
    }

    /**
     * Get count of additional skills beyond featured ones
     */
    const getShowMoreSkillsCount = async (featuredCount: number = FEATURED_SKILLS_COUNT, filters?: string) => {
        const allSkills = await keywordSearch('skills', '', {
            hitsPerPage: 50,
            filterBy: filters
        })
        const countNextSkills = allSkills.nbHits - featuredCount
        return countNextSkills > 0 ? countNextSkills : 0
    }

    /**
     * Load skill by ID
     */
    const loadSkill = (id: SkillId) => {
        const firebase = getFirebaseContext('loadSkill')
        return firebase.$firebase.loadDocumentOrFail<Skill>(firebase.collections.skills, id)
    }

    /**
     * Load skill by slug or ID
     */
    const loadSkillBySlugOrId = (slug: string) => {
        const firebase = getFirebaseContext('loadSkillBySlugOrId')
        return firebase.$firebase.loadDocumentByAttrOrId<Skill>(firebase.collections.skills, 'slug', slug)
    }

    /**
     * List skills by organization ID
     */
    const listSkillsByOrgId = async (orgId: OrgId | OrgId[]): Promise<Skill[]> => {
        const firebase = getFirebaseContext('listSkillsByOrgId')
        const orgIds = ([] as OrgId[]).concat(orgId)

        // XXX: Firestore query only support where-in clause with up to 10 values to compare
        const chunkedOrgIds = chunk(orgIds, 10)
        const chunkedPromises = chunkedOrgIds.map((orgIds) => {
            return firebase.$firebase.listDocuments<Skill>(
                query(firebase.collections.skills, where('orgId', 'in', orgIds))
            )
        })

        const skills = (await Promise.all(chunkedPromises)).flat()

        return orderByDate(skills)
    }

    /**
     * Create a new skill
     */
    const createSkill = async (skill: SkillFormData, orgId: OrgId): Promise<Skill> => {
        const firebase = getFirebaseContext('createSkill')
        const data: any = {
            ...skill,
            orgId,
            visibility: skill.visibility || VisibilityType.unlisted,
            published: false
        }

        // Create document with timestamps and author info
        const docRef = await firebase.$firebase.createDocument<Skill>(firebase.collections.skills, data, {
            withTimestamps: true,
            withAuthor: true,
            withSoftDelete: true
        })

        // Load and return the created skill
        return await firebase.$firebase.loadDocumentOrFail<Skill>(firebase.collections.skills, docRef.id)
    }

    /**
     * Update a skill
     */
    const updateSkill = async (id: SkillId, data: Partial<Skill>): Promise<void> => {
        const firebase = getFirebaseContext('updateSkill')

        await firebase.$firebase.updateDocument<Skill>(firebase.collections.skills, id, data, {
            withTimestamps: true,
            withAuthor: true
        })
    }

    /**
     * Update a skill's visibility
     */
    const updateSkillVisibility = async (
        id: SkillId,
        action: VisibilityChangeAction,
        value?: VisibilityType
    ): Promise<void> => {
        const firebase = getFirebaseContext('updateSkillVisibility')

        try {
            const updateData: Partial<Skill> = {}

            if (value) {
                updateData.visibility = value
            }

            // Handle different visibility change actions
            switch (action) {
                case VisibilityChangeAction.PUBLISH:
                    updateData.visibility = VisibilityType.published
                    updateData.published = true
                    break
                case VisibilityChangeAction.UNPUBLISH:
                    updateData.visibility = VisibilityType.unlisted
                    updateData.published = false
                    break
                case VisibilityChangeAction.CHANGE:
                    // For CHANGE action, use the provided value
                    if (value) {
                        updateData.visibility = value
                        updateData.published = value === VisibilityType.published
                    }
                    break
                case VisibilityChangeAction.NONE:
                    // No changes needed
                    return
            }

            await firebase.$firebase.updateDocument<Skill>(firebase.collections.skills, id, updateData, {
                withTimestamps: true,
                withAuthor: true
            })
        } catch (error: any) {
            console.error('Failed to update skill visibility:', error)
            throw error
        }
    }

    /**
     * Load members of a skill
     */
    const loadSkillMembers = async (skill: Skill): Promise<UserProfile[]> => {
        const firebase = getFirebaseContext('loadSkillMembers')

        if (!skill.memberIds || skill.memberIds.length === 0) {
            return []
        }

        try {
            // Query membership requests for this skill to get members
            const membershipRequests = await firebase.$firebase.listDocuments(
                query(
                    firebase.collections.membershipRequests,
                    where('skillId', '==', skill.id),
                    where('status', '==', 'accepted')
                )
            )

            // Get user profiles for accepted members
            const memberIds = membershipRequests.map((request) => request.userId).filter(Boolean)
            const memberProfiles: UserProfile[] = []

            // Load user profiles in chunks to avoid Firestore limitations
            const chunkedMemberIds = chunk(memberIds, 10)
            for (const memberIdChunk of chunkedMemberIds) {
                const profiles = await firebase.$firebase.listDocuments<UserProfile>(
                    query(firebase.collections.profiles, where('id', 'in', memberIdChunk))
                )
                memberProfiles.push(...profiles)
            }

            return memberProfiles
        } catch (error) {
            console.error('Failed to load skill members:', error)
            return []
        }
    }

    /**
     * Format a skill's date
     */
    const formatSkillDate = (skill: Skill): string => {
        const isEventOrWorkgroup = skill.type === SkillType.event || skill.type === SkillType.workgroup
        return formatDate(skill.startDate) + (skill.time && isEventOrWorkgroup ? ` o ${skill.time} (CET)` : '')
    }

    /**
     * Find chat ID for a skill
     */
    const findChatIdForSkill = async (skillId: SkillId, userId: UserId): Promise<ChatId | undefined> => {
        const firebase = getFirebaseContext('findChatIdForSkill')

        // Ensure we have valid parameters before querying
        if (!skillId || !userId) {
            return undefined
        }

        const topic = getTopicKey(ChatTopicBase.skill, skillId)

        // Additional check for valid topic
        if (!topic) {
            return undefined
        }

        try {
            // Query chats collection for matching topic and user participation
            const chats = await firebase.$firebase.listDocuments(
                query(
                    firebase.collections.chats,
                    where('topic', '==', topic),
                    where('participantIds', 'array-contains', userId)
                )
            )

            const firstChat = chats[0]
            return firstChat ? (firstChat.id as ChatId) : undefined
        } catch (error) {
            console.warn('Failed to find chat for skill:', error)
            return undefined
        }
    }

    /**
     * Create an empty skill object
     */
    const makeEmptySkill = (): Partial<SkillFormData> => {
        return {
            title: '',
            type: 'event',
            desc: '',
            author: '',
            image: '',
            contentURL: '',
            contentURLType: '',
            liveURL: '',
            liveDate: '',
            slido: '',
            category: '',
            authorBio: '',
            content: '',
            who: [],
            topics: [],
            format: '',
            time: '',
            placeCategory: '',
            placeAddress: '',
            placeOnline: '',
            priceCategory: '',
            price: null,
            capacityCategory: '',
            capacity: null,
            lessons: [],
            published: false,
            visibility: VisibilityType.unlisted
        }
    }

    /**
     * Load skills for the current user
     */
    const loadUserSkills = async (): Promise<Skill[]> => {
        const firebase = getFirebaseContext('loadUserSkills')

        try {
            const userId = firebase.$firebase.getCurrentUserId()
            if (!userId) {
                return []
            }

            // Query skills created by the current user
            const userSkills = await firebase.$firebase.listDocuments<Skill>(
                query(firebase.collections.skills, where('createdById', '==', userId))
            )

            return orderByDate(userSkills)
        } catch (error) {
            console.error('Failed to load user skills from Firestore:', error)
            return []
        }
    }

    return {
        // Core functionality
        searchSkills,
        searchSkillsPersonalized,
        loadSkillsFacets,
        getFeaturedSkills,
        getShowMoreSkillsCount,
        loadSkill,
        loadSkillBySlugOrId,
        listSkillsByOrgId,
        createSkill,
        updateSkill,
        updateSkillVisibility,
        loadSkillMembers,
        formatSkillDate,
        findChatIdForSkill,
        makeEmptySkill,
        loadUserSkills,

        // Constants
        DEFAULT_PER_PAGE,
        FEATURED_SKILLS_COUNT
    }
}
